import React, {
    MouseEventHandler,
    useCallback,
    useState
} from 'react';
import { Spinner, Size, Color } from '@vtblife/uikit/legacy';
import Icon from '@vtblife/uikit-icons';

import classname from '@search/classname/src';
import { ImageLazySeo } from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySeo';
import { CoreSvgSpriteIcon } from '@search/vtbeco-frontend-core/view/common/components/CoreSvgSpriteIcon/CoreSvgSpriteIcon';

import { IImage } from '..';
import { useControl } from './useControl';
import './styles.css';

interface IProps {
    elements: IImage[];
    title?: string;
    index?: number;
    className?: string;
    onClickMain: (index: number) => void;
}

const cn = classname.bind(null, 'CarouselGallery');

const Arrow = ({ type, handleClick }: {
    type: 'prev' | 'next';
    handleClick: MouseEventHandler;
}) => {
    return (
        <div
            className={cn('nav-area', { type })}
            onClick={handleClick}
        >
            <button
                type='button'
                className={cn('btn', { type })}
            >
                <CoreSvgSpriteIcon name='galleryArrow' />
            </button>
        </div>
    );
};

const saved = new Map<string, boolean>();

const GalleryImage = ({
    onImageClick,
    element,
    isFirstOffer
}: {
    onImageClick: () => void;
    element?: IImage;
    /**
     * Для уменьшения LCP видимым на первом экране картинкам лучше НЕ задавать lazy.
     * Больше инфы тут https://web.dev/lcp-lazy-loading/
     */
    isFirstOffer?: boolean;
}) => {
    const src = element?.large ?? element?.src2x ?? element?.src;
    const id = element?.originPath ?? src;
    const isVerticalInitial = id ? saved.get(id) : undefined;
    const isLoading = isVerticalInitial === undefined;

    const [ state, setState ] = useState({ isLoading, isVertical: isVerticalInitial ?? false });
    const finishLoading = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.target as (HTMLImageElement | undefined);
        const isVertical = ((target?.naturalWidth ?? 1) / (target?.naturalHeight ?? 1)) <= 1;

        id && saved.set(id, isVertical);

        setState({ isLoading: false, isVertical });
    }, [ id ]);

    if (! element) {
        return (
            <div className={cn('empty-container')}>
                <Icon name='camera-crossed' size='xl' />
            </div>
        );
    }

    return (
        <div
            onClick={onImageClick}
            className={cn('img-container')}
        >
            {
                state.isLoading ? (
                    <div className={cn('spinner-container')}>
                        <Spinner
                            size={Size.Medium}
                            velocity='normal'
                            color={Color.White}
                        />
                    </div>
                ) : null
            }
            <ImageLazySeo
                onError={finishLoading}
                onLoad={finishLoading}
                src={src}
                originPath={element?.originPath}
                srcWidth={954}
                srcHeight={533}
                imgClassName={cn('hero-img', {
                    invisible: state.isLoading,
                    contain: element.attributesType === 'LAYOUT' || state.isVertical,
                    isLayout: element.attributesType === 'LAYOUT'
                })}
                className={cn('heroImgWrapper', { isLayout: element.attributesType === 'LAYOUT' })}
                loadingLazy={isFirstOffer ? 'eager' : 'native'}
                alt={element.description}
                title={element.description}
            />
        </div>
    );
};

const CarouselGallery: React.FunctionComponent<IProps> = ({
    index,
    elements,
    onClickMain,
    className
}) => {
    const {
        currentIndex,
        handlePrevClick,
        handleNextClick
    } = useControl({
        isKeyNav: false,
        index: index ?? 0,
        lastIndex: elements.length - 1
    });

    const handleClickMain = useCallback(() => {
        onClickMain?.(currentIndex);
    }, [ onClickMain, currentIndex ]);

    const isSwipeable = elements.length > 1;

    const currentElement = elements[currentIndex];
    const preloadImages: typeof elements = [];

    if (elements.length > 1) {
        const prevImage = elements[Math.max(0, currentIndex - 1)];
        const nextImage = elements[Math.min(elements.length - 1, currentIndex + 1)];

        prevImage !== currentElement && preloadImages.push(prevImage);
        nextImage !== currentElement && preloadImages.push(nextImage);
    }

    return (
        <div className={cn(null, undefined, className)}>
            <GalleryImage
                isFirstOffer={currentIndex === 0}
                key={currentElement?.id ?? currentIndex}
                element={currentElement}
                onImageClick={handleClickMain}
            />
            <div style={{ display: 'hidden' }}>
                {preloadImages.map(element => (
                    <ImageLazySeo
                        key={element.originPath}
                        src={element?.large ?? element?.src2x ?? element?.src}
                        originPath={element.originPath}
                        srcWidth={954}
                        srcHeight={533}
                    />
                ))}
            </div>
            {isSwipeable && (
                <>
                    <Arrow
                        type='prev'
                        handleClick={handlePrevClick}
                    />
                    <Arrow
                        type='next'
                        handleClick={handleNextClick}
                    />
                </>
            )}
        </div>
    );
};

export default CarouselGallery;
